/**
 * API client for VisionLive backend
 */

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.vltryon.com/api';

// Helper for making authenticated requests
async function fetchWithAuth(endpoint: string, options: RequestInit = {}) {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.message || error.error || 'Request failed');
  }
  
  return response.json();
}

// ============================================
// AUTH
// ============================================

export const auth = {
  async getSession() {
    return fetchWithAuth('/auth/get-session');
  },
  
  async signOut() {
    const response = await fetch(`${API_BASE}/auth/sign-out`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    // Sign-out may return empty response, so don't try to parse JSON
    return { success: response.ok };
  },
  
  // Better-Auth uses POST to /sign-in/social with provider in body
  async signInWithGoogle() {
    const response = await fetch(`${API_BASE}/auth/sign-in/social`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ 
        provider: 'google',
        callbackURL: window.location.origin + '/dashboard',
      }),
    });
    const data = await response.json();
    if (data.url) {
      // Redirect to Google OAuth
      window.location.href = data.url;
    }
    return data;
  },
};

// ============================================
// USER
// ============================================

export const user = {
  async getProfile() {
    return fetchWithAuth('/user/profile');
  },
  
  async getApiKey() {
    return fetchWithAuth('/user/api-key');
  },
  
  async regenerateApiKey() {
    return fetchWithAuth('/user/api-key/regenerate', { method: 'POST' });
  },
  
  async getDomains() {
    return fetchWithAuth('/user/domains');
  },
  
  async addDomain(domain: string) {
    return fetchWithAuth('/user/domains', {
      method: 'POST',
      body: JSON.stringify({ domain }),
    });
  },
  
  async removeDomain(id: string) {
    return fetchWithAuth(`/user/domains/${id}`, { method: 'DELETE' });
  },
  
  async deleteAccount() {
    return fetchWithAuth('/user/account', { method: 'DELETE' });
  },

  async getWidgetSettings(): Promise<{
    languageMode: string;
    locale: string;
    showCategoryFilter?: boolean;
    showGenderFilter?: boolean;
  }> {
    return fetchWithAuth('/user/widget-settings');
  },

  async updateWidgetSettings(data: {
    languageMode?: 'auto' | 'manual';
    locale?: string;
    showCategoryFilter?: boolean;
    showGenderFilter?: boolean;
  }) {
    return fetchWithAuth('/user/widget-settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};

// ============================================
// PRODUCTS
// ============================================

export type ProductCategory = 'dioptric' | 'sunglasses';
export type ProductGender = 'male' | 'female' | 'unisex';

export interface Product {
  id: string;
  sku: string;
  name: string | null;
  assetType: '3d' | '2d';
  modelUrl: string | null;
  frontImageUrl: string | null;
  handleImageUrl: string | null;
  thumbnailUrl: string | null;
  addToCartUrl: string | null; // URL for add to cart button in widget
  category?: ProductCategory;
  gender?: ProductGender; // male | female | unisex
  frameWidth: number; // Frame front width in mm
  handleWidth: number; // Temple/handle length in mm
  bridgeLengthCm: number; // Bridge length in cm
  templeLength: number;
  templeHingeOffset: number;
  templeYOffset: number;
  templeHandleHeight: number;
  // Asset padding meta (computed from alpha-bounds; used for scale correction)
  frontContentScaleX?: number;
  frontContentScaleY?: number;
  frontContentBBox?: string | null; // JSON string {xMin,yMin,xMax,yMax}
  handleContentScaleX?: number;
  handleContentScaleY?: number;
  handleContentBBox?: string | null; // JSON string {xMin,yMin,xMax,yMax}
  // Manual multiplier to fine-tune sizing (1.0 = none)
  scaleCorrection?: number;
  // Clip-on attachment
  clipOnImageUrl?: string | null;
  clipOnHingeOffset?: number;
  clipOnYOffset?: number;
  recommendedFaceShapes: string[];
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProductInput {
  sku: string;
  name?: string;
  assetType: '3d' | '2d';
  modelUrl?: string;
  frontImageUrl?: string;
  handleImageUrl?: string;
  thumbnailUrl?: string;
  addToCartUrl?: string;
  category?: ProductCategory;
  gender?: ProductGender;
  frameWidth?: number;
  handleWidth?: number; // Temple/handle length in mm
  bridgeLengthCm?: number; // Bridge length in cm
  templeLength?: number;
  templeHingeOffset?: number;
  templeYOffset?: number;
  templeHandleHeight?: number;
  frontContentScaleX?: number;
  frontContentScaleY?: number;
  frontContentBBox?: string | null;
  handleContentScaleX?: number;
  handleContentScaleY?: number;
  handleContentBBox?: string | null;
  scaleCorrection?: number;
  recommendedFaceShapes?: string[];
  // Clip-on attachment
  clipOnImageUrl?: string | null;
  clipOnHingeOffset?: number;
  clipOnYOffset?: number;
}

export interface ProductsListResponse {
  products: Product[];
  total: number;
  page: number;
  limit: number;
}

export const products = {
  async list(
    category?: 'all' | 'dioptric' | 'sunglasses',
    gender?: 'all' | 'male' | 'female',
    opts?: { page?: number; limit?: number; q?: string; all?: boolean }
  ): Promise<ProductsListResponse> {
    const params = new URLSearchParams();
    if (category && category !== 'all') params.set('category', category);
    if (gender && gender !== 'all') params.set('gender', gender);
    if (opts?.page != null) params.set('page', String(opts.page));
    if (opts?.limit != null) params.set('limit', String(opts.limit));
    if (opts?.q != null && opts.q.trim()) params.set('q', opts.q.trim());
    if (opts?.all) params.set('all', '1');
    const qs = params.toString() ? `?${params.toString()}` : '';
    return fetchWithAuth(`/vto/products${qs}`);
  },
  
  async create(data: CreateProductInput): Promise<{ product: Product }> {
    return fetchWithAuth('/vto/products', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  
  async update(id: string, data: Partial<CreateProductInput>): Promise<void> {
    return fetchWithAuth(`/vto/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  
  async delete(id: string): Promise<void> {
    return fetchWithAuth(`/vto/products/${id}`, { method: 'DELETE' });
  },

  async reorder(productIds: string[]): Promise<void> {
    return fetchWithAuth('/vto/products/reorder', {
      method: 'PUT',
      body: JSON.stringify({ productIds }),
    });
  },
  
  async getUploadUrl(filename: string, contentType: string, type: 'model' | 'front' | 'handle' | 'thumbnail') {
    return fetchWithAuth('/vto/upload-url', {
      method: 'POST',
      body: JSON.stringify({ filename, contentType, type }),
    });
  },
  
  async uploadModel(file: File): Promise<{ publicUrl: string; key: string; filename: string }> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_BASE}/vto/upload-model`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.message || error.error || 'Upload failed');
    }
    
    return response.json();
  },
};

// ============================================
// DASHBOARD
// ============================================

export const dashboard = {
  async getStats() {
    return fetchWithAuth('/dashboard/stats');
  },
};

// ============================================
// ANALYTICS
// ============================================

export interface VisitorStats {
  period: string;
  startDate: string;
  totals: {
    widget: { uniqueVisitors: number; totalViews: number };
    dashboard: { uniqueVisitors: number; totalViews: number };
  };
  daily: Array<{ date: string; widget: number; dashboard: number }>;
}

export interface TopProduct {
  productId: string;
  totalViews: number;
  productName: string | null;
  productSku: string;
  thumbnailUrl: string | null;
}

export interface AnalyticsSummary {
  today: { uniqueVisitors: number; totalViews: number };
  thisWeek: { uniqueVisitors: number; totalViews: number };
}

export const analytics = {
  async getVisitors(period: 'today' | 'week' | 'month' = 'week'): Promise<VisitorStats> {
    return fetchWithAuth(`/analytics/visitors?period=${period}`);
  },
  
  async getTopProducts(limit: number = 10): Promise<{ products: TopProduct[] }> {
    return fetchWithAuth(`/analytics/products/top?limit=${limit}`);
  },
  
  async getSummary(): Promise<AnalyticsSummary> {
    return fetchWithAuth('/analytics/summary');
  },
  
  async trackBatch(events: Array<{ page: string; timestamp: number }>) {
    return fetchWithAuth('/analytics/batch', {
      method: 'POST',
      body: JSON.stringify({ events }),
    });
  },
};

// ============================================
// ADMIN (Superadmin only)
// ============================================

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  image: string | null;
  plan: string;
  planUpdatedAt: string | null;
  planUpdatedBy: string | null;
  apiKey: string | null;
  createdAt: string;
  updatedAt: string;
  productCount: number;
  domainCount: number;
}

export interface AdminUserDetail {
  user: AdminUser;
  products: Product[];
  domains: Array<{ id: string; domain: string; createdAt: string }>;
  analytics: {
    last30Days: { uniqueVisitors: number; totalViews: number };
  };
}

export interface AdminStats {
  users: {
    total: number;
    byPlan: Record<string, number>;
  };
  products: {
    total: number;
  };
  today: {
    widgetLoads: number;
    uniqueVisitors: number;
  };
}

export const admin = {
  async checkAccess(): Promise<{ isSuperAdmin: boolean }> {
    try {
      return await fetchWithAuth('/admin/check');
    } catch {
      return { isSuperAdmin: false };
    }
  },
  
  async getStats(): Promise<AdminStats> {
    return fetchWithAuth('/admin/stats');
  },
  
  async getUsers(page: number = 1, limit: number = 20, search?: string): Promise<{
    users: AdminUser[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    return fetchWithAuth(`/admin/users?${params}`);
  },
  
  async getUser(id: string): Promise<AdminUserDetail> {
    return fetchWithAuth(`/admin/users/${id}`);
  },
  
  async updateUserPlan(id: string, plan: 'free' | 'starter' | 'advanced' | 'business'): Promise<void> {
    return fetchWithAuth(`/admin/users/${id}/plan`, {
      method: 'PUT',
      body: JSON.stringify({ plan }),
    });
  },
  
  async impersonateUser(id: string): Promise<{ token: string; expiresIn: number; user: { id: string; name: string; email: string } }> {
    return fetchWithAuth(`/admin/impersonate/${id}`, { method: 'POST' });
  },
};

// ============================================
// SUBSCRIPTION (Whop.com integration)
// ============================================

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  monthlyPrice: number | null;
  annualPrice: number | null;
  annualTotal?: number;
  popular?: boolean;
  features: string[];
  limits: {
    products: number;
    vtoSessions: number;
  };
  contactSales?: boolean;
}

export interface SubscriptionStatus {
  plan: string;
  status: 'active' | 'cancelled' | 'past_due' | 'trialing' | null;
  billingCycle: 'monthly' | 'annual' | null;
  periodEnd: string | null;
  whopMembershipId: string | null;
  limits: {
    products: number;
    vtoSessions: number;
  };
}

export const subscription = {
  /**
   * Get available subscription plans
   */
  async getPlans(): Promise<{ plans: SubscriptionPlan[] }> {
    return fetchWithAuth('/subscription/plans');
  },
  
  /**
   * Get current subscription status (requires auth)
   */
  async getStatus(): Promise<SubscriptionStatus> {
    return fetchWithAuth('/subscription/status');
  },
  
  /**
   * Create checkout session (can be called with or without auth)
   * If not authenticated, email must be provided
   */
  async checkout(
    plan: 'starter' | 'advanced',
    billingCycle: 'monthly' | 'annual',
    email?: string
  ): Promise<{ checkoutUrl: string }> {
    const response = await fetch(`${API_BASE}/subscription/checkout`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan, billingCycle, email }),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.message || error.error || 'Failed to create checkout');
    }
    
    return response.json();
  },

  /**
   * Create checkout session for one-time setup (e.g. catalog setup – 25 products)
   */
  async checkoutSetup(type: 'setup_25' = 'setup_25'): Promise<{ checkoutUrl: string }> {
    const response = await fetch(`${API_BASE}/subscription/checkout`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.message || error.error || 'Failed to create checkout');
    }

    return response.json();
  },
  
  /**
   * Cancel subscription (requires auth)
   */
  async cancel(): Promise<{ message: string; periodEnd: string | null }> {
    return fetchWithAuth('/subscription/cancel', { method: 'POST' });
  },
  
  /**
   * Get billing portal URL (requires auth)
   */
  async getPortalUrl(): Promise<{ portalUrl: string }> {
    return fetchWithAuth('/subscription/portal');
  },
};

// ============================================
// BLOG (public, no auth required)
// ============================================

export interface BlogPostSummary {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  mainImageUrl: string | null;
  publishedAt: string | null;
  createdAt: string;
  authorId: string;
  authorName: string | null;
}

export interface BlogPostFull extends BlogPostSummary {
  content: string;
}

export interface BlogListResponse {
  posts: BlogPostSummary[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export const blog = {
  async list(page: number = 1, limit: number = 10, q?: string): Promise<BlogListResponse> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (q) params.set('q', q);
    const response = await fetch(`${API_BASE}/blog/posts?${params}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(err.message || err.error || 'Failed to load blog');
    }
    return response.json();
  },

  async getBySlug(slug: string): Promise<{ post: BlogPostFull }> {
    const response = await fetch(`${API_BASE}/blog/posts/slug/${encodeURIComponent(slug)}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Post not found');
      const err = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(err.message || err.error || 'Failed to load post');
    }
    return response.json();
  },
};

// Blog admin (superadmin only, requires auth)
export const blogAdmin = {
  async listAll(page: number = 1, limit: number = 20): Promise<BlogListResponse> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    return fetchWithAuth(`/blog/admin/posts?${params}`);
  },
  async getById(id: string): Promise<{ post: BlogPostFull }> {
    return fetchWithAuth(`/blog/admin/posts/${id}`);
  },
  async create(data: { title: string; excerpt?: string; content: string; slug?: string; published?: boolean; mainImageUrl?: string }) {
    return fetchWithAuth('/blog/posts', { method: 'POST', body: JSON.stringify(data) });
  },
  async update(id: string, data: { title?: string; excerpt?: string; content?: string; slug?: string; published?: boolean; mainImageUrl?: string }) {
    return fetchWithAuth(`/blog/posts/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async delete(id: string) {
    return fetchWithAuth(`/blog/posts/${id}`, { method: 'DELETE' });
  },
};

// ============================================
// AUTH (Extended for email/password)
// ============================================

export const authExtended = {
  ...auth,
  
  async signUp(email: string, password: string, name: string) {
    const response = await fetch(`${API_BASE}/auth/sign-up/email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, name }),
    });
    const data = await response.json();
    
    // Better Auth returns 422 for validation errors, but still includes data
    // Check status and structure response accordingly
    if (!response.ok) {
      // If there's an error in the data, return it
      if (data.error) {
        return { error: data.error };
      }
      // Otherwise create a generic error
      return { 
        error: { 
          message: data.message || `Registration failed (${response.status})` 
        } 
      };
    }
    
    return data;
  },
  
  async signIn(email: string, password: string) {
    const response = await fetch(`${API_BASE}/auth/sign-in/email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    const data = await response.json();
    return { ...data, _status: response.status };
  },
  
  async forgotPassword(email: string) {
    const response = await fetch(`${API_BASE}/auth/request-password-reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, redirectTo: window.location.origin + '/reset-password' }),
    });
    return response.json();
  },
  
  async resetPassword(token: string, newPassword: string) {
    const response = await fetch(`${API_BASE}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword }),
    });
    return response.json();
  },
  
  async resendVerification(email: string) {
    const response = await fetch(`${API_BASE}/auth/send-verification-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, callbackURL: window.location.origin + '/dashboard' }),
    });
    return response.json();
  },
};

